import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { supabase } from "../supabaseClient";
import { useSeason } from "./SeasonContext";

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within SettingsProvider");
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const { selectedSeason, registrationSeason } = useSeason();
  const [settings, setSettings] = useState(null);
  const [registrationSettings, setRegistrationSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wrap loadSettings in useCallback to stabilize it
  const loadSettings = useCallback(async (seasonId) => {
    setLoading(true);
    const { data, error } = await supabase
      .from("league_settings")
      .select("*")
      .eq("season_id", seasonId)
      .single();

    if (!error && data) {
      setSettings(data);
    } else if (error?.code === "PGRST116") {
      // No settings found - create default
      await createDefaultSettings(seasonId);
    }
    setLoading(false);
  }, []);

  // Load registration settings separately
  const loadRegistrationSettings = useCallback(async (seasonId) => {
    const { data, error } = await supabase
      .from("league_settings")
      .select("*")
      .eq("season_id", seasonId)
      .single();

    if (!error && data) {
      setRegistrationSettings(data);
    }
  }, []);

  const createDefaultSettings = async (seasonId) => {
    // Produce YYYY-MM-DD in local time
    const localDateStr = (offsetDays) => {
      const d = new Date();
      d.setDate(d.getDate() + offsetDays);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };

    const defaultSettings = {
      season_id: seasonId,
      early_bird_deadline: localDateStr(30),
      registration_deadline: localDateStr(45),
      season_start_date: localDateStr(60),
      early_bird_price: 1450,
      regular_price: 1600,
      additional_player_fee: 125,
      ref_fee_coverage: 375,
      min_players: 5,
      max_players: 15,
      base_player_count: 8,
      min_age: 17,
      max_non_muslim_players: 2,
      regular_season_games: 15,
      rules: {
        jerseys_provided: true,
        videos_provided: true,
        all_star_game: true,
        islamic_reminders: true,
        playoff_guaranteed: true,
        no_pro_players: true,
        no_d1_players: true,
      },
      rules_url: null,
    };

    const { data, error } = await supabase
      .from("league_settings")
      .insert(defaultSettings)
      .select()
      .single();

    if (!error && data) {
      setSettings(data);
    }
  };

  useEffect(() => {
    if (selectedSeason?.id) {
      loadSettings(selectedSeason.id);
    }
  }, [selectedSeason?.id, loadSettings]); // Now loadSettings is stable

  // Load registration settings when registrationSeason changes
  useEffect(() => {
    if (registrationSeason?.id) {
      loadRegistrationSettings(registrationSeason.id);
    }
  }, [registrationSeason?.id, loadRegistrationSettings]);

  const updateSettings = async (updates) => {
    if (!settings?.id)
      return { success: false, error: "No settings to update" };

    const { data, error } = await supabase
      .from("league_settings")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", settings.id)
      .select()
      .single();

    if (!error && data) {
      setSettings(data);
      return { success: true };
    }

    return { success: false, error };
  };

  // Helper functions for common checks
  const isEarlyBird = () => {
    if (!settings?.early_bird_deadline) return false;
    return new Date() <= new Date(settings.early_bird_deadline);
  };

  const isRegistrationOpen = () => {
    // Use registrationSettings if available (for registration page), otherwise fall back to settings
    const settingsToCheck = registrationSettings || settings;
    if (!settingsToCheck?.registration_deadline) return true;
    return new Date() <= new Date(settingsToCheck.registration_deadline);
  };

  const getCurrentPrice = () => {
    if (!settings) return 0;
    return isEarlyBird() ? settings.early_bird_price : settings.regular_price;
  };

  const value = {
    settings,
    registrationSettings,
    loading,
    loadSettings,
    updateSettings,
    isEarlyBird,
    isRegistrationOpen,
    getCurrentPrice,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
